import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { ArrowRight, BookOpen } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { API } from '@/lib/api';

const EASE = [0.22, 1, 0.36, 1];

const formatDate = (iso) => {
  try {
    return new Date(iso).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' });
  } catch {
    return '';
  }
};

/**
 * Score a post against a list of keywords. Matches are weighted:
 *  - exact tag match: +5
 *  - title contains keyword: +3
 *  - excerpt contains keyword: +1
 */
function scorePost(post, keywords) {
  if (!keywords?.length) return 0;
  const lcTags = (post.tags || []).map((t) => (t || '').toLowerCase());
  const lcTitle = (post.title || '').toLowerCase();
  const lcExcerpt = (post.excerpt || '').toLowerCase();
  let score = 0;
  for (const kw of keywords) {
    const k = kw.toLowerCase().trim();
    if (!k) continue;
    if (lcTags.some((t) => t === k || t.includes(k))) score += 5;
    if (lcTitle.includes(k)) score += 3;
    if (lcExcerpt.includes(k)) score += 1;
  }
  return score;
}

/**
 * Related-articles widget. Displays up to `limit` published posts
 * ranked by keyword relevance. Excludes the current post via `excludeSlug`.
 */
export function RelatedArticles({ keywords = [], limit = 3, excludeSlug, title = 'À lire aussi' }) {
  const [posts, setPosts] = useState(null);

  useEffect(() => {
    axios
      .get(`${API}/blog/posts?limit=60`)
      .then((r) => setPosts(r.data || []))
      .catch(() => setPosts([]));
  }, []);

  if (!posts || posts.length === 0) return null;

  const filtered = posts.filter((p) => p.slug !== excludeSlug);
  const ranked = filtered
    .map((p) => ({ ...p, __score: scorePost(p, keywords) }))
    .sort((a, b) => {
      if (b.__score !== a.__score) return b.__score - a.__score;
      return new Date(b.created_at || 0) - new Date(a.created_at || 0);
    })
    .slice(0, limit);

  if (ranked.length === 0) return null;

  return (
    <section className="section-padding bg-background border-t border-border" data-testid="related-articles">
      <div className="container-custom max-w-6xl">
        <div className="flex items-center gap-2 mb-8">
          <BookOpen className="h-5 w-5 text-primary" />
          <span className="text-primary font-semibold text-xs tracking-[0.3em] uppercase">━━ Articles liés</span>
        </div>
        <h2 className="font-heading text-2xl md:text-3xl text-dark mb-8 tracking-wide">{title}</h2>

        <div className="grid md:grid-cols-3 gap-6">
          {ranked.map((p, i) => (
            <motion.div
              key={p.slug}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-50px' }}
              transition={{ duration: 0.6, delay: i * 0.08, ease: EASE }}
            >
              <Link to={`/blog/${p.slug}`} className="group block h-full" data-testid={`related-article-${p.slug}`}>
                <Card className="overflow-hidden h-full flex flex-col hover:shadow-xl hover:-translate-y-1 transition-all duration-300 border-border/60">
                  {p.cover_image && (
                    <div className="aspect-[16/10] overflow-hidden bg-muted">
                      <img decoding="async"
                        src={p.cover_image}
                        alt={p.title}
                        loading="lazy"
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                  )}
                  <div className="p-5 flex-1 flex flex-col">
                    <div className="flex flex-wrap gap-1.5 mb-3">
                      {(p.tags || []).slice(0, 2).map((t) => (
                        <Badge key={t} variant="outline" className="text-[10px]">
                          {t}
                        </Badge>
                      ))}
                    </div>
                    <h3 className="font-heading text-base text-dark group-hover:text-primary transition-colors tracking-wide mb-2 line-clamp-2">
                      {p.title}
                    </h3>
                    <p className="text-sm text-muted-foreground line-clamp-3 mb-4 flex-1">{p.excerpt}</p>
                    <div className="flex items-center justify-between text-xs text-muted-foreground">
                      <span>{formatDate(p.created_at)}</span>
                      <span className="inline-flex items-center gap-1 text-primary group-hover:translate-x-1 transition-transform">
                        Lire <ArrowRight className="h-3 w-3" />
                      </span>
                    </div>
                  </div>
                </Card>
              </Link>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
